import { type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FormBackButton } from "@/ui/FormBackButton";
import { StatusBadge, type StatusVariant } from "@/ui/StatusBadge";
import { MobileNavMenu, type MobileNavLink } from "@/ui/MobileNavMenu";

interface DetailHeaderProps {
  /** Back navigation path */
  backTo: string;
  /** Back button label (after "Voltar") */
  backLabel?: string;
  /** Page title */
  title?: string;
  /** Subtitle (rendered below title). Aceita string ou ReactNode (chips, glossário, etc.) */
  subtitle?: ReactNode;
  /** Status badge props */
  status?: { variant: StatusVariant; label: string };
  /** Action buttons (right side) */
  actions?: ReactNode;
  /**
   * Primary actions duplicadas em uma barra inferior fixa no mobile (≤640 px).
   * Quando informado, `actions` é escondido no mobile (`hidden sm:flex`) e estas
   * ações ficam num footer sticky com touch targets ≥44 px (Fitts's Law).
   * Use no máximo 2-3 botões. Em desktop é ignorado.
   */
  mobileActions?: ReactNode;
  /**
   * Toolbar compacta mobile `[← Voltar] [título] [⋯ menu]` exibida ACIMA do
   * header em telas estreitas (`sm:hidden`). Use para concentrar navegação
   * secundária contextual (ex.: "Ver lista", "Ver no ciclo") que de outra
   * forma quebraria em múltiplas linhas. O `backTo` aqui pode divergir do
   * `backTo` principal (que continua governando o botão Voltar desktop).
   */
  mobileNav?: {
    title?: string;
    backTo: string;
    backLabel?: string;
    links?: MobileNavLink[];
  };
}

export function DetailHeader({ backTo, backLabel, title, subtitle, status, actions, mobileActions, mobileNav }: DetailHeaderProps) {
  const hasMobileBar = !!mobileActions;

  return (
    <>
      {mobileNav && (
        <MobileNavMenu
          title={mobileNav.title}
          backTo={mobileNav.backTo}
          backLabel={mobileNav.backLabel}
          links={mobileNav.links}
        />
      )}
      <div className="relative px-3 sm:px-4 py-3 mb-2 rounded-xl bg-card border border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
            <FormBackButton to={backTo} label={backLabel ?? ""} />
            {title && (
              <>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">{title}</h1>
                  {subtitle && (
                    typeof subtitle === "string"
                      ? <p className="text-xs sm:text-sm text-muted-foreground truncate">{subtitle}</p>
                      : <div className="text-xs sm:text-sm text-muted-foreground">{subtitle}</div>
                  )}
                </div>
                {status && <StatusBadge status={status.variant} label={status.label} />}
              </>
            )}
          </div>
          {actions && (
            <div
              className={
                hasMobileBar
                  ? "hidden md:flex items-center gap-2 flex-wrap md:flex-nowrap md:shrink-0"
                  : "flex items-center gap-2 flex-wrap md:flex-nowrap md:shrink-0 [&>button]:min-h-[44px] md:[&>button]:min-h-0"
              }
            >
              {actions}
            </div>
          )}
        </div>
      </div>
      {hasMobileBar && typeof document !== "undefined" && createPortal(
        <div
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-3 py-2 flex items-center gap-2 *:flex-1 [&>button]:min-h-[44px] [&>button]:w-full"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
          role="toolbar"
          aria-label="Ações principais"
        >
          {mobileActions}
        </div>,
        document.body
      )}
    </>
  );
}
